import { useCallback, useEffect, useState } from "react";
import { Warning, Siren, Fire, Drop, FirstAid, Plus, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatCard, StatusBadge } from "@/components/Shared";
import { MultiImageField } from "@/components/MultiImageField";
import { AuthImage } from "@/components/AuthImage";
import { COUNTRIES } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const label = (v) => String(v || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const KIT_TONES = {
  ok: "bg-[#E6F7F1] text-[#0B7A5B] border-[#A7E3CE]",
  needs_service: "bg-[#FFF7DB] text-[#8A6A00] border-[#F2DE9B]",
  faulty: "bg-[#FFECEB] text-[#B3241B] border-[#F6B9B5]",
  missing: "bg-[#FFECEB] text-[#B3241B] border-[#F6B9B5]",
};
const KitBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center border px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wider ${KIT_TONES[status] || "bg-slate-100 text-slate-600 border-slate-300"}`}>
    {label(status)}
  </span>
);
const RISKS = ["none", "low", "medium", "high", "severe"];
const emptySite = { name: "", country: "US", city: "", address: "", muster_point: "", flood_risk: "none", earthquake_zone: "none", fire_risk: "low", sprinklers: false, hydrant_note: "", emergency_number: "", warden_name: "", warden_phone: "", headcount: 0, images: [], editId: null };
const emptyPlan = { hazard: "fire", site_id: "none", title: "", stepsText: "", muster_point: "", contactsText: "", published: true };
const emptyIncident = { hazard: "fire", site_id: "none", severity: "minor", title: "", description: "", people_affected: 0, damage_note: "", images: [] };
const emptyDrill = { hazard: "fire", site_id: "none", held_on: "", evacuation_seconds: 0, attendees: 0, notes: "", next_due: "" };
const emptyKit = { kind: "extinguisher", code: "", site_id: "none", location: "", last_inspected: "", next_due: "", status: "ok", notes: "" };
const emptyAlert = { hazard: "fire", site_id: "none", headline: "", instruction: "" };

export default function Safety() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isExternal = ["agent", "lender", "scheme_officer", "legal_advisor", "service_provider"].includes(user?.role);
  const [tab, setTab] = useState("plans");
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sites, setSites] = useState([]);
  const [plans, setPlans] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [drills, setDrills] = useState([]);
  const [kit, setKit] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [site, setSite] = useState(emptySite);
  const [plan, setPlan] = useState(emptyPlan);
  const [incident, setIncident] = useState(emptyIncident);
  const [drill, setDrill] = useState(emptyDrill);
  const [item, setItem] = useState(emptyKit);
  const [alert, setAlert] = useState(emptyAlert);
  const [update, setUpdate] = useState({ incident: null, note: "", status: "" });
  const [inspect, setInspect] = useState({ item: null, status: "ok", inspected_on: "", next_due: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const get = async (url, setter, fallback) => {
      try {
        const res = await api.get(url);
        setter(res.data);
      } catch {
        setter(fallback);
      }
    };
    try {
      const [m, s] = await Promise.all([api.get("/safety/meta"), api.get("/safety/summary")]);
      setMeta(m.data);
      setSummary(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    await Promise.all([
      get("/safety/sites", setSites, []),
      get("/safety/plans", setPlans, []),
      get("/safety/alerts", setAlerts, []),
      get("/safety/incidents", setIncidents, []),
      get("/safety/drills", setDrills, []),
      get("/safety/equipment", setKit, []),
    ]);
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
      setDialog(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const hazards = meta?.hazards || [];
  const siteId = (v) => (v === "none" ? null : v);
  const activeAlert = alerts.find((a) => a.active);
  const shareLink = (t) => `${window.location.origin}/safety/${t}`;

  return (
    <div data-testid="safety-page">
      <PageHeader
        overline="Safety"
        title="Safety & emergency"
        description="Fire, flood, earthquake and every other emergency — the plan, the kit, the drills and what to do when it happens."
        action={
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <Button variant="outline" className="border-[#B3241B] text-[#B3241B]" data-testid="raise-alert-button" onClick={() => { setAlert(emptyAlert); setDialog("alert"); }}>
                <Siren size={16} className="mr-1.5" /> Raise an alert
              </Button>
            )}
            <Button data-testid="report-incident-button" onClick={() => { setIncident(emptyIncident); setDialog("incident"); }} className={isExternal ? "hidden" : ""}>
              <Plus size={16} className="mr-1.5" /> Report an incident
            </Button>
          </div>
        }
      />

      {activeAlert && (
        <div data-testid="active-alert-banner" className="mb-5 border-l-4 border-[#B3241B] bg-[#FFECEB] p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="overline text-[0.6rem] text-[#B3241B]">
                Emergency · {label(activeAlert.hazard)}{activeAlert.site_name ? ` · ${activeAlert.site_name}` : ""}
              </div>
              <div className="mt-1 font-display text-lg font-bold text-[#7A1712]">{activeAlert.headline}</div>
              <p className="mt-1 whitespace-pre-line text-sm text-[#7A1712]">{activeAlert.instruction}</p>
            </div>
            {isAdmin && (
              <Button size="sm" variant="outline" data-testid="stand-down-button" onClick={() => run(() => api.post(`/safety/alerts/${activeAlert.id}/stand-down`), "Stood down — all clear sent")}>
                Stand down
              </Button>
            )}
          </div>
        </div>
      )}

      {summary && (
        <div className="mb-5 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4" data-testid="safety-stats">
          <StatCard label="Open incidents" value={summary.open_incidents} sub={`${summary.serious_incidents} serious or worse`} icon={Warning} tone={summary.open_incidents ? "danger" : "default"} testid="stat-open-incidents" />
          <StatCard label="Kit overdue" value={summary.equipment_overdue} sub={`past inspection · ${summary.equipment_faulty} faulty or missing`} icon={FirstAid} tone={summary.equipment_overdue ? "danger" : "success"} testid="stat-kit-overdue" />
          <StatCard label="Drills due" value={summary.drills_due} sub="past their next due date" icon={Fire} tone={summary.drills_due ? "danger" : "default"} testid="stat-drills-due" />
          <StatCard label="Sites covered" value={summary.sites} sub={`${summary.plans} published procedures`} icon={Drop} tone="primary" testid="stat-sites" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          {[["plans", `Plans (${plans.length})`], ["incidents", `Incidents (${incidents.length})`],
            ["drills", `Drills (${drills.length})`], ["equipment", `Kit (${kit.length})`],
            ["sites", `Sites (${sites.length})`]]
            .filter(([v]) => !(isExternal && ["incidents", "drills", "equipment"].includes(v)))
            .map(([v, t]) => (
            <TabsTrigger key={v} value={v} data-testid={`safety-tab-${v}`} className="rounded-none text-xs uppercase tracking-wider">{t}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === "plans" && (
        <div data-testid="plans-panel">
          {isAdmin && (
            <Button variant="outline" size="sm" className="mb-3" data-testid="add-plan-button" onClick={() => { setPlan(emptyPlan); setDialog("plan"); }}>
              Write a procedure
            </Button>
          )}
          {plans.length === 0 ? (
            <EmptyState testid="no-plans" title="No procedures yet" description="An admin can publish the fire, flood and earthquake plans here." />
          ) : (
            <div className="space-y-3">
              {plans.map((p) => (
                <div key={p.id} data-testid={`plan-${p.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="overline text-[0.6rem] text-[#B3241B]">{label(p.hazard)}{p.site_name ? ` · ${p.site_name}` : " · All sites"}</div>
                      <div className="mt-1 text-sm font-semibold text-[#0F172A]">{p.title}</div>
                    </div>
                    {isAdmin && (
                      <button type="button" data-testid={`delete-plan-${p.id}`} onClick={() => run(() => api.delete(`/safety/plans/${p.id}`), "Procedure removed")} className="text-xs text-[#B3241B] hover:underline">
                        Remove
                      </button>
                    )}
                  </div>
                  <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-relaxed text-slate-700">
                    {(p.steps || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                  {p.muster_point && <p className="mt-2 text-xs text-slate-500">Muster point: {p.muster_point}</p>}
                  {(p.contacts || []).length > 0 && <p className="mt-1 text-xs text-slate-500">Contacts: {p.contacts.join(" · ")}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "incidents" && (
        <div data-testid="incidents-panel">
          {incidents.length === 0 ? (
            <EmptyState testid="no-incidents" title="No incidents logged" description="Report a fire, flood, quake damage or a near miss — photos help." />
          ) : (
            <div className="space-y-3">
              {incidents.map((i) => (
                <div key={i.id} data-testid={`incident-${i.id}`} className="border border-border bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">{i.title}</div>
                      <div className="text-xs text-slate-500">
                        {i.reference} · {label(i.hazard)} · {label(i.severity)}{i.site_name ? ` · ${i.site_name}` : ""} · {i.reporter_name}
                      </div>
                    </div>
                    <StatusBadge value={i.status} testid={`incident-status-${i.id}`} />
                  </div>
                  <p className="mt-2 whitespace-pre-line text-xs text-slate-600">{i.description}</p>
                  {(i.image_ids || []).length > 0 && (
                    <div className="mt-3 grid gap-2 sm:grid-cols-3" data-testid={`incident-photos-${i.id}`}>
                      {i.image_ids.map((id) => (
                        <AuthImage key={id} imageId={id} alt="Incident photo" className="h-28 w-full border border-border object-cover" testid={`incident-photo-${i.id}-${id}`} />
                      ))}
                    </div>
                  )}
                  {(i.updates || []).map((u, k) => (
                    <div key={k} data-testid={`incident-update-${i.id}-${k}`} className="mt-2 border border-border p-3 text-xs text-slate-600">
                      <span className="font-semibold">{u.by} · {label(u.status)}</span>
                      <div className="mt-1 whitespace-pre-line">{u.note}</div>
                    </div>
                  ))}
                  {i.status !== "closed" && (
                    <Button size="sm" className="mt-3" data-testid={`update-incident-${i.id}`} onClick={() => { setUpdate({ incident: i, note: "", status: i.status }); setDialog("update"); }}>
                      Post an update
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "drills" && (
        <div data-testid="drills-panel">
          {isAdmin && (
            <Button variant="outline" size="sm" className="mb-3" data-testid="add-drill-button" onClick={() => { setDrill(emptyDrill); setDialog("drill"); }}>
              Log a drill
            </Button>
          )}
          {drills.length === 0 ? (
            <EmptyState testid="no-drills" title="No drills logged" description="Record every fire, flood and earthquake drill with the evacuation time." />
          ) : (
            <div className="border border-border bg-white">
              {drills.map((d) => (
                <div key={d.id} data-testid={`drill-${d.id}`} className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4 last:border-0">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0F172A]">{label(d.hazard)} drill · {d.held_on}</div>
                    <div className="text-xs text-slate-500">
                      {d.site_name || "All sites"} · {d.attendees} attended · cleared in {Math.floor((d.evacuation_seconds || 0) / 60)}m {(d.evacuation_seconds || 0) % 60}s
                      {d.next_due ? ` · next due ${d.next_due}` : ""}
                    </div>
                    {d.notes && <p className="mt-1 text-xs text-slate-600">{d.notes}</p>}
                  </div>
                  {isAdmin && (
                    <button type="button" data-testid={`delete-drill-${d.id}`} onClick={() => run(() => api.delete(`/safety/drills/${d.id}`), "Drill removed")} className="text-xs text-[#B3241B] hover:underline">
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "equipment" && (
        <div data-testid="equipment-panel">
          {isAdmin && (
            <Button variant="outline" size="sm" className="mb-3" data-testid="add-equipment-button" onClick={() => { setItem(emptyKit); setDialog("kit"); }}>
              Add safety kit
            </Button>
          )}
          {kit.length === 0 ? (
            <EmptyState testid="no-equipment" title="Nothing in the register" description="Add extinguishers, alarms, first aid kits and flood pumps with their inspection dates." />
          ) : (
            <div className="border border-border bg-white">
              {kit.map((k) => (
                <div key={k.id} data-testid={`equipment-${k.id}`} className={`flex flex-wrap items-center justify-between gap-2 border-b border-border p-4 last:border-0 ${k.is_overdue ? "bg-[#FFF7F6]" : ""}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0F172A]">{label(k.kind)} · {k.code}</div>
                    <div className="text-xs text-slate-500">
                      {[k.site_name, k.location].filter(Boolean).join(" · ") || "Location not set"}
                      {k.last_inspected ? ` · checked ${k.last_inspected}` : ""}
                      {k.next_due ? ` · due ${k.next_due}` : ""}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {k.is_overdue && <StatusBadge value="urgent" testid={`equipment-overdue-${k.id}`} />}
                    <KitBadge status={k.status} testid={`equipment-status-${k.id}`} />
                    {isAdmin && (
                      <>
                        <Button size="sm" variant="outline" data-testid={`inspect-equipment-${k.id}`} onClick={() => { setInspect({ item: k, status: k.status, inspected_on: "", next_due: "", notes: "" }); setDialog("inspect"); }}>
                          Inspect
                        </Button>
                        <button type="button" data-testid={`delete-equipment-${k.id}`} onClick={() => run(() => api.delete(`/safety/equipment/${k.id}`), "Removed from the register")} className="text-xs text-[#B3241B] hover:underline">
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "sites" && (
        <div data-testid="sites-panel">
          {isAdmin && (
            <Button variant="outline" size="sm" className="mb-3" data-testid="add-site-button" onClick={() => { setSite(emptySite); setDialog("site"); }}>
              Add a site
            </Button>
          )}
          {sites.length === 0 ? (
            <EmptyState testid="no-sites" title="No sites yet" description="Add your yard or plant with its flood risk, earthquake zone and muster point." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {sites.map((s) => (
                <div key={s.id} data-testid={`site-${s.id}`} className="border border-border bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A]">{s.name}</div>
                      <div className="text-xs text-slate-500">{[s.city, s.country].filter(Boolean).join(", ")}</div>
                    </div>
                    {isAdmin && (
                      <div className="flex shrink-0 items-center gap-2">
                        <button type="button" data-testid={`edit-site-${s.id}`} onClick={() => { setSite({ ...emptySite, ...s, city: s.city || "", address: s.address || "", muster_point: s.muster_point || "", hydrant_note: s.hydrant_note || "", emergency_number: s.emergency_number || "", warden_name: s.warden_name || "", warden_phone: s.warden_phone || "", images: (s.image_ids || []).map((id) => ({ image_id: id, caption: "" })), editId: s.id }); setDialog("site"); }} className="text-xs text-[#002FA7] hover:underline">
                          Edit
                        </button>
                        <button type="button" data-testid={`delete-site-${s.id}`} onClick={() => run(() => api.delete(`/safety/sites/${s.id}`), "Site removed")} className="text-xs text-[#B3241B] hover:underline">
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    {[["Fire", s.fire_risk], ["Flood", s.flood_risk], ["Quake", s.earthquake_zone]].map(([l, v]) => (
                      <div key={l}>
                        <dt className="overline text-[0.6rem] text-slate-400">{l}</dt>
                        <dd className="text-[#0F172A]">{label(v)}</dd>
                      </div>
                    ))}
                  </dl>
                  {s.muster_point && <p className="mt-2 text-xs text-slate-600">Muster point: {s.muster_point}</p>}
                  {(s.emergency_number || s.warden_name || s.warden_phone) && (
                    <p data-testid={`site-contacts-${s.id}`} className="mt-1 text-xs text-slate-600">
                      {[s.emergency_number ? `Emergency ${s.emergency_number}` : null,
                        [s.warden_name, s.warden_phone].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  {(s.image_ids || []).length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {s.image_ids.map((id) => (
                        <AuthImage key={id} imageId={id} alt="Evacuation map" className="h-24 w-full border border-border object-cover" testid={`site-map-${s.id}-${id}`} />
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    data-testid={`share-site-${s.id}`}
                    onClick={() => { navigator.clipboard?.writeText(shareLink(s.public_token)); toast.success("Public safety link copied"); }}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:underline"
                  >
                    <ShareNetwork size={14} /> Copy public safety link
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Report an incident */}
      <Dialog open={dialog === "incident"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Report an incident</DialogTitle>
            <DialogDescription>Fire, flood, quake damage, spill or a near miss — an admin is notified straight away.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>What happened (title)</Label><Input data-testid="incident-title-input" value={incident.title} onChange={(e) => setIncident({ ...incident, title: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Hazard</Label>
              <Select value={incident.hazard} onValueChange={(v) => setIncident({ ...incident, hazard: v })}>
                <SelectTrigger data-testid="incident-hazard-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{hazards.map((h) => <SelectItem key={h} value={h}>{meta?.hazard_labels?.[h] || label(h)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>How serious</Label>
              <Select value={incident.severity} onValueChange={(v) => setIncident({ ...incident, severity: v })}>
                <SelectTrigger data-testid="incident-severity-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.severities || []).map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Site</Label>
              <Select value={incident.site_id} onValueChange={(v) => setIncident({ ...incident, site_id: v })}>
                <SelectTrigger data-testid="incident-site-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="none">Not site specific</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>People affected</Label><Input data-testid="incident-people-input" type="number" min="0" value={incident.people_affected} onChange={(e) => setIncident({ ...incident, people_affected: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>Describe it</Label><Textarea data-testid="incident-description-input" rows={5} value={incident.description} onChange={(e) => setIncident({ ...incident, description: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>Damage note</Label><Input data-testid="incident-damage-input" value={incident.damage_note} onChange={(e) => setIncident({ ...incident, damage_note: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2">
              <MultiImageField testid="incident-photos" images={incident.images} max={6} onChange={(images) => setIncident((i) => ({ ...i, images }))} />
            </div>
          </div>
          <DialogFooter className="mobile-sticky-footer">
            <Button data-testid="submit-incident-button" disabled={busy || !incident.title} onClick={() => run(async () => {
              await api.post("/safety/incidents", {
                hazard: incident.hazard, site_id: siteId(incident.site_id), severity: incident.severity,
                title: incident.title, description: incident.description,
                people_affected: Number(incident.people_affected) || 0,
                damage_note: incident.damage_note || null,
                image_ids: incident.images.map((i) => i.image_id),
              });
              setTab("incidents");
            }, "Incident reported")}>
              Report it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incident update */}
      <Dialog open={dialog === "update"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Post an update</DialogTitle><DialogDescription>{update.incident?.reference}</DialogDescription></DialogHeader>
          <Textarea data-testid="incident-update-input" rows={4} value={update.note} onChange={(e) => setUpdate({ ...update, note: e.target.value })} className="rounded-none border-slate-300" />
          {isAdmin && (
            <div>
              <Label>Status</Label>
              <Select value={update.status} onValueChange={(v) => setUpdate({ ...update, status: v })}>
                <SelectTrigger data-testid="incident-update-status" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.incident_statuses || []).map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="submit-update-button" disabled={busy || update.note.trim().length < 2} onClick={() => run(() => api.post(`/safety/incidents/${update.incident.id}/updates`, { note: update.note, status: isAdmin ? update.status : null }), "Update posted")}>
              Post it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Procedure */}
      <Dialog open={dialog === "plan"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader><DialogTitle>Write a procedure</DialogTitle><DialogDescription>One action step per line, in the order people should do them.</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Title</Label><Input data-testid="plan-title-input" value={plan.title} onChange={(e) => setPlan({ ...plan, title: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Hazard</Label>
              <Select value={plan.hazard} onValueChange={(v) => setPlan({ ...plan, hazard: v })}>
                <SelectTrigger data-testid="plan-hazard-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{hazards.map((h) => <SelectItem key={h} value={h}>{meta?.hazard_labels?.[h] || label(h)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Site</Label>
              <Select value={plan.site_id} onValueChange={(v) => setPlan({ ...plan, site_id: v })}>
                <SelectTrigger data-testid="plan-site-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="none">All sites</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Action steps (one per line)</Label><Textarea data-testid="plan-steps-input" rows={7} value={plan.stepsText} onChange={(e) => setPlan({ ...plan, stepsText: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Muster point</Label><Input data-testid="plan-muster-input" value={plan.muster_point} onChange={(e) => setPlan({ ...plan, muster_point: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Contacts (comma separated)</Label><Input data-testid="plan-contacts-input" value={plan.contactsText} onChange={(e) => setPlan({ ...plan, contactsText: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-plan-button" disabled={busy || !plan.title} onClick={() => run(() => api.post("/safety/plans", {
              hazard: plan.hazard, site_id: siteId(plan.site_id), title: plan.title,
              steps: plan.stepsText.split("\n").map((s) => s.trim()).filter(Boolean),
              muster_point: plan.muster_point || null,
              contacts: plan.contactsText.split(",").map((s) => s.trim()).filter(Boolean),
              published: true,
            }), "Procedure published")}>
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drill */}
      <Dialog open={dialog === "drill"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Log a drill</DialogTitle><DialogDescription>Keep the record of every practice evacuation.</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Hazard</Label>
              <Select value={drill.hazard} onValueChange={(v) => setDrill({ ...drill, hazard: v })}>
                <SelectTrigger data-testid="drill-hazard-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{hazards.map((h) => <SelectItem key={h} value={h}>{meta?.hazard_labels?.[h] || label(h)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Site</Label>
              <Select value={drill.site_id} onValueChange={(v) => setDrill({ ...drill, site_id: v })}>
                <SelectTrigger data-testid="drill-site-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="none">All sites</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Held on</Label><Input data-testid="drill-date-input" type="date" value={drill.held_on} onChange={(e) => setDrill({ ...drill, held_on: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Next due</Label><Input data-testid="drill-next-due-input" type="date" value={drill.next_due} onChange={(e) => setDrill({ ...drill, next_due: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Evacuation time (seconds)</Label><Input data-testid="drill-seconds-input" type="number" min="0" value={drill.evacuation_seconds} onChange={(e) => setDrill({ ...drill, evacuation_seconds: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Attended</Label><Input data-testid="drill-attendees-input" type="number" min="0" value={drill.attendees} onChange={(e) => setDrill({ ...drill, attendees: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Textarea data-testid="drill-notes-input" rows={3} value={drill.notes} onChange={(e) => setDrill({ ...drill, notes: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-drill-button" disabled={busy || !drill.held_on} onClick={() => run(() => api.post("/safety/drills", {
              hazard: drill.hazard, site_id: siteId(drill.site_id), held_on: drill.held_on,
              evacuation_seconds: Number(drill.evacuation_seconds) || 0,
              attendees: Number(drill.attendees) || 0,
              notes: drill.notes || null, next_due: drill.next_due || null,
            }), "Drill logged")}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Equipment */}
      <Dialog open={dialog === "kit"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Add safety kit</DialogTitle><DialogDescription>Extinguishers, alarms, first aid, flood pumps and generators.</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Kind</Label>
              <Select value={item.kind} onValueChange={(v) => setItem({ ...item, kind: v })}>
                <SelectTrigger data-testid="kit-kind-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{(meta?.equipment_kinds || []).map((k) => <SelectItem key={k} value={k}>{label(k)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Code / tag</Label><Input data-testid="kit-code-input" value={item.code} onChange={(e) => setItem({ ...item, code: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Site</Label>
              <Select value={item.site_id} onValueChange={(v) => setItem({ ...item, site_id: v })}>
                <SelectTrigger data-testid="kit-site-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="none">Not set</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Location</Label><Input data-testid="kit-location-input" value={item.location} onChange={(e) => setItem({ ...item, location: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Last inspected</Label><Input data-testid="kit-last-input" type="date" value={item.last_inspected} onChange={(e) => setItem({ ...item, last_inspected: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Next due</Label><Input data-testid="kit-due-input" type="date" value={item.next_due} onChange={(e) => setItem({ ...item, next_due: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Input data-testid="kit-notes-input" value={item.notes} onChange={(e) => setItem({ ...item, notes: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-kit-button" disabled={busy || !item.code} onClick={() => run(() => api.post("/safety/equipment", {
              kind: item.kind, code: item.code, site_id: siteId(item.site_id),
              location: item.location || null, last_inspected: item.last_inspected || null,
              next_due: item.next_due || null, status: item.status, notes: item.notes || null,
            }), "Added to the register")}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inspection */}
      <Dialog open={dialog === "inspect"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record an inspection</DialogTitle><DialogDescription>{inspect.item ? `${label(inspect.item.kind)} ${inspect.item.code}` : ""}</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Condition</Label>
              <Select value={inspect.status} onValueChange={(v) => setInspect({ ...inspect, status: v })}>
                <SelectTrigger data-testid="inspect-status-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.equipment_statuses || []).map((s) => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Inspected on</Label><Input data-testid="inspect-date-input" type="date" value={inspect.inspected_on} onChange={(e) => setInspect({ ...inspect, inspected_on: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Next due</Label><Input data-testid="inspect-next-due-input" type="date" value={inspect.next_due} onChange={(e) => setInspect({ ...inspect, next_due: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>Notes</Label><Input data-testid="inspect-notes-input" value={inspect.notes} onChange={(e) => setInspect({ ...inspect, notes: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-inspect-button" disabled={busy} onClick={() => run(() => api.post(`/safety/equipment/${inspect.item.id}/inspections`, {
              status: inspect.status, inspected_on: inspect.inspected_on || null,
              next_due: inspect.next_due || null, notes: inspect.notes || null,
            }), "Inspection recorded")}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Site */}
      <Dialog open={dialog === "site"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader><DialogTitle>{site.editId ? "Edit site" : "Add a site"}</DialogTitle><DialogDescription>Risk levels, muster point and the contacts people need in an emergency.</DialogDescription></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Site name</Label><Input data-testid="site-name-input" value={site.name} onChange={(e) => setSite({ ...site, name: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div>
              <Label>Country</Label>
              <Select value={site.country} onValueChange={(v) => setSite({ ...site, country: v })}>
                <SelectTrigger data-testid="site-country-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{COUNTRIES.map(([code, name]) => <SelectItem key={code} value={code}>{name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>City</Label><Input data-testid="site-city-input" value={site.city} onChange={(e) => setSite({ ...site, city: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input data-testid="site-address-input" value={site.address} onChange={(e) => setSite({ ...site, address: e.target.value })} className="rounded-none border-slate-300" /></div>
            {[["fire_risk", "Fire risk"], ["flood_risk", "Flood risk"], ["earthquake_zone", "Earthquake zone"]].map(([key, lab]) => (
              <div key={key}>
                <Label>{lab}</Label>
                <Select value={site[key]} onValueChange={(v) => setSite({ ...site, [key]: v })}>
                  <SelectTrigger data-testid={`site-${key}-select`} className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>{RISKS.map((r) => <SelectItem key={r} value={r}>{label(r)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
            <div><Label>People on site</Label><Input data-testid="site-headcount-input" type="number" min="0" value={site.headcount} onChange={(e) => setSite({ ...site, headcount: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>Muster point</Label><Input data-testid="site-muster-input" value={site.muster_point} onChange={(e) => setSite({ ...site, muster_point: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Emergency number</Label><Input data-testid="site-emergency-input" value={site.emergency_number} onChange={(e) => setSite({ ...site, emergency_number: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Hydrant / sprinkler note</Label><Input data-testid="site-hydrant-input" value={site.hydrant_note} onChange={(e) => setSite({ ...site, hydrant_note: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Warden name</Label><Input data-testid="site-warden-input" value={site.warden_name} onChange={(e) => setSite({ ...site, warden_name: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div><Label>Warden phone</Label><Input data-testid="site-warden-phone-input" value={site.warden_phone} onChange={(e) => setSite({ ...site, warden_phone: e.target.value })} className="rounded-none border-slate-300" /></div>
            <label className="flex items-center gap-2 text-xs text-slate-600 sm:col-span-2">
              <input type="checkbox" data-testid="site-sprinklers-checkbox" checked={site.sprinklers} onChange={(e) => setSite({ ...site, sprinklers: e.target.checked })} />
              Sprinklers fitted
            </label>
            <div className="sm:col-span-2">
              <MultiImageField testid="site-maps" images={site.images} max={5} onChange={(images) => setSite((s) => ({ ...s, images }))} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-site-button" disabled={busy || !site.name} onClick={() => run(async () => {
              const { editId, images, ...rest } = site;
              const body = { ...rest, headcount: Number(site.headcount) || 0, image_ids: images.map((i) => i.image_id) };
              delete body.id;
              delete body.public_token;
              delete body.created_at;
              delete body.updated_at;
              delete body.image_ids_legacy;
              if (editId) await api.patch(`/safety/sites/${editId}`, body);
              else await api.post("/safety/sites", body);
            }, site.editId ? "Site updated" : "Site added")}>
              Save site
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emergency alert */}
      <Dialog open={dialog === "alert"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Raise an emergency alert</DialogTitle>
            <DialogDescription>Everyone gets an in-app alert straight away; admins are emailed too.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Hazard</Label>
              <Select value={alert.hazard} onValueChange={(v) => setAlert({ ...alert, hazard: v })}>
                <SelectTrigger data-testid="alert-hazard-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{hazards.map((h) => <SelectItem key={h} value={h}>{meta?.hazard_labels?.[h] || label(h)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Site</Label>
              <Select value={alert.site_id} onValueChange={(v) => setAlert({ ...alert, site_id: v })}>
                <SelectTrigger data-testid="alert-site-select" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="none">All sites</SelectItem>
                  {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Headline</Label><Input data-testid="alert-headline-input" value={alert.headline} onChange={(e) => setAlert({ ...alert, headline: e.target.value })} className="rounded-none border-slate-300" /></div>
            <div className="sm:col-span-2"><Label>What people must do</Label><Textarea data-testid="alert-instruction-input" rows={4} value={alert.instruction} onChange={(e) => setAlert({ ...alert, instruction: e.target.value })} className="rounded-none border-slate-300" /></div>
          </div>
          <DialogFooter>
            <Button className="bg-[#B3241B] hover:bg-[#8E1C15]" data-testid="submit-alert-button" disabled={busy || !alert.headline} onClick={() => run(() => api.post("/safety/alerts", {
              hazard: alert.hazard, site_id: siteId(alert.site_id),
              headline: alert.headline, instruction: alert.instruction,
            }), "Alert sent to everyone")}>
              Send the alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
